/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2020 - 2020
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import {
  isAfter,
  isFuture,
  isValid,
  parseISO,
  differenceInMinutes,
  getHours,
  setHours,
  getMinutes,
  setMinutes,
  isSameDay,
} from "date-fns";
import { format, utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { FormHelperText } from "@material-ui/core";

import MenuItem from "@material-ui/core/MenuItem";
import InputLabel from "@material-ui/core/InputLabel";
import Grid from "@material-ui/core/Grid";
import Switch from "@material-ui/core/Switch";
import FormGroup from "@material-ui/core/FormGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import CircularProgress from "@material-ui/core/CircularProgress";

import theme from "../../theme/muiTheme";
import { ThemeProvider } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import Tooltip from "@material-ui/core/Tooltip";

import styled from "styled-components";
import StyledFormControl from "../common/StyledFormControl";
import StyledSelect from "../common/StyledSelect";
import StyledSelectDisabled from "../common/StyledSelectDisabled";
import StyledTextField from "../common/StyledTextField";
import BaseButton from "../common/BaseButton";
import StyledKeyboardDatePicker from "../common/StyledKeyboardDatePicker";
import StyledKeyboardTimePicker from "../common/StyledKeyboardTimePicker";
import types from "../../helpers/types";
import { fetchCoveredRoutes } from "../../slices/coveredRoutesSlice";
import { fetchLegs } from "../../slices/legsSlice";
import { fetchNotes } from "../../slices/notesSlice";
import { fetchLegTrailers } from "../../slices/legTrailersSlice";
import { putLegUpdate, putLegDeparture } from "../../api/api";
import LocationAutoComplete from "../common/LocationAutoComplete";
import ProblemCodeAutoComplete from "./ProblemCodeAutoComplete";

import { getETA, } from "../../api/api";

import { formatTimeWithTimeZoneAndYear, utcToZonedDateTime } from "../../helpers/dateHelpers";

const StyledInfoIcon = styled(InfoOutlinedIcon)`
  font-size: 12px !important;
  margin: 0 3px !important;
`;

//Styling for the *Optional label
const optionalLabelStyle = {
  fontSize: ".7rem",
  margin: 0,
}

//Styling for the *ETA Miles label
const etaDivLabel = {
  fontSize: ".8rem",
  margin: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}

const etaLabelStyle = {
  fontSize: ".8rem",
  margin: 0,
}

const getDefaultLegUpdateType = (currentLeg, previousLeg) => {
  let defaultLegUpdateType = types.legUpdateTypes.miscellaneousNote;

  if (currentLeg.legStatus === types.legStatuses.active) {
    defaultLegUpdateType = types.legUpdateTypes.checkCall;
  } else if (
    currentLeg.legStatus === types.legStatuses.ready &&
    (!previousLeg ||
      (previousLeg && previousLeg.legStatus === types.legStatuses.complete))
  ) {
    defaultLegUpdateType = types.legUpdateTypes.departure;
  }

  return defaultLegUpdateType;
};

const noProblemCode = types.problemCodes[0].id;

function TrackingUpdateForm({
  currentLeg,
  openedRoute,
  previousLeg,
  setShowSuccessNotification,
  setUnsavedChanges,
}) {
  const dispatch = useDispatch();

  const { legTrailers } = useSelector((state) => state.legTrailers);

  const [trackingUpdateRequestStatus, setTrackingUpdateRequestStatus] =
    useState(types.requestStatuses.initial);
  const [defaultLegUpdateType, setDefaultLegUpdateType] = useState(
    getDefaultLegUpdateType(currentLeg, previousLeg)
  );
  const [legUpdateType, setLegUpdateType] = useState(defaultLegUpdateType);
  const [legMoveType, setLegMoveType] = useState(0);

  const [trailerNumber1, setTrailerNumber1] = useState("");
  const [trailerNumber2, setTrailerNumber2] = useState("");
  const [trailerId1, setTrailerId1] = useState("");
  const [trailerId2, setTrailerId2] = useState("");

  const [startDateActualUserLocal, setStartDateActualUserLocal] =
    useState(null);
  const [startTimeOnlyActualUserLocal, setStartTimeOnlyActualUserLocal] =
    useState(null);

  const [arriveDateActualUserLocal, setArriveDateActualUserLocal] =
    useState(null);
  const [arriveTimeOnlyActualUserLocal, setArriveTimeOnlyActualUserLocal] =
    useState(null);

  const [estimatedArriveDateActualUserLocal, setEstimatedArriveDateActualUserLocal] =
    useState(null);
  const [estimatedArriveTimeOnlyActualUserLocal, setEstimatedArriveTimeOnlyActualUserLocal] =
    useState(null);

  const trackingUpdateInProgress =
    trackingUpdateRequestStatus === types.requestStatuses.inProgress;
  const [location, setLocation] = useState("");
  const [autoETAChecked, setAutoETAChecked] = useState(false);
  const [note, setNote] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [problemCodeId, setProblemCodeId] = useState(
    currentLeg.problemCodeId ?? noProblemCode
  );
  const [shutdown, setShutdown] = useState(currentLeg.shutdown ?? false);
  const [showETAMiles, setShowETAMiles] = useState(false);
  const [showETALoading, setShowETALoading] = useState(false);
  const [etaMilesRemaining, setETAMilesRemaining] = useState(9999);
  const { filters } = useSelector((state) => state);
  const nowZonedOriginFacility = utcToZonedTime(
    Date.now(),
    currentLeg.originFacilityTimeZoneName
  );

  const originFacilityTimeZoneNameShort = format(Date.now(), "z", {
    timeZone: currentLeg.originFacilityTimeZoneName
      ? currentLeg.originFacilityTimeZoneName
      : "UTC",
  });
  const nowZonedDestinationFacility = utcToZonedTime(
    Date.now(),
    currentLeg.destinationFacilityTimeZoneName
  );
  const destinationFacilityTimeZoneNameShort = format(Date.now(), "z", {
    timeZone: currentLeg.destinationFacilityTimeZoneName
      ? currentLeg.destinationFacilityTimeZoneName
      : "UTC",
  });

  const customizedTimeTextField = (timezone) => {
    return (
      <span
        style={{ marginRight: 8, color: "rgb(73, 90, 99)" }}
        data-testid="movement-origin-facility-time-zone-name"
      >
        {timezone}
      </span>
    );
  };

  const handleLegUpdateSelectChange = (event) => {
    const legUpdate = Number(event.target.value);
    setLegUpdateType(legUpdate);
    if (legUpdate === types.legUpdateTypes.arrival) {
      setShutdown(false);
    }
    setValidationErrors({});
    setUnsavedChanges(true);
  };

  const handleMoveTypeSelectChange = (event) => {
    setLegMoveType(event.target.value);
  };

  const handleDateOrTimePickerChange =
    (validationProperty, setDateTime) => (pickedDate, stringValue) => {
      setValidationErrors({
        ...validationErrors,
        [validationProperty]: false,
      });

      if (isValid(pickedDate)) {
        setDateTime(pickedDate);
        setUnsavedChanges(true);
      }
      else if (stringValue) {
        setValidationErrors({
          ...validationErrors,
          [validationProperty]: true,
        });
      }
      else {
        setDateTime(null);
      }
    };

  const handleLocationChange = async (event, option) => {
    setValidationErrors({ ...validationErrors, location: false });
    setLocation(option);
    setUnsavedChanges(true);
    await updateETAFields(autoETAChecked, option);
  };

  const updateETAFields = async (autoETASwitchState, locationStr) => {
    if (autoETASwitchState && locationStr !== null && locationStr !== '') {
      setShowETALoading(true);
      setEstimatedArriveDateActualUserLocal(null);
      setEstimatedArriveTimeOnlyActualUserLocal(null);

      const eta = await getETA(currentLeg.id, locationStr);

      if (eta.legID === currentLeg.id) {
        let destinationArrivalInDestinationTimezone = formatTimeWithTimeZoneAndYear(
          eta.etaUtc,
          currentLeg.destinationFacilityTimeZoneName
        );

        setEstimatedArriveDateActualUserLocal(destinationArrivalInDestinationTimezone);
        setEstimatedArriveTimeOnlyActualUserLocal(destinationArrivalInDestinationTimezone);

        setShowETALoading(false);
        setETAMilesRemaining(eta.remainingDistanceMiles);
        setShowETAMiles(true);
      }
      else {
        setEstimatedArriveDateActualUserLocal(null);
        setEstimatedArriveTimeOnlyActualUserLocal(null);
        setShowETALoading(false);
        setShowETAMiles(false);
        setETAMilesRemaining(9999);
      }
    }
    else {
      setEstimatedArriveDateActualUserLocal(null);
      setEstimatedArriveTimeOnlyActualUserLocal(null);
      setShowETALoading(false);
      setShowETAMiles(false);
      setETAMilesRemaining(9999);
    }
  }

  const handleAutoETAChange = async (event) => {
    let newAutoETAState = !autoETAChecked;
    setAutoETAChecked(newAutoETAState);
    await updateETAFields(newAutoETAState, location); // Used newly swapped value for autoETA
  }

  const handleNoteTextFieldChange = (event) => {
    setValidationErrors({ ...validationErrors, note: false });
    setNote(event.target.value);
    setUnsavedChanges(true);
  };

  const validateTrailerNumber = (trailerNumber) => {
    if(!trailerNumber){
      return false;
    }
    
    if (trailerNumber.length < 1) {
      return false;
    }

    // ALl numbers & letters allowed but at least one required
    const validTrailerNumber = new RegExp('^[a-zA-Z0-9]+$');

    let response = validTrailerNumber.test(trailerNumber);
    return response;
  }

  const handleTrailerNumber1Change = (event) => {
    let trailerNumber1Validation = validateTrailerNumber(event.target.value);
    setValidationErrors({ ...validationErrors, trailerNumber1: !trailerNumber1Validation });
    setTrailerNumber1(event.target.value);
    setUnsavedChanges(true);
  };

  const handleTrailerNumber2Change = (event) => {
    let trailerNumber2Validation = validateTrailerNumber(event.target.value);
    setValidationErrors({ ...validationErrors, trailerNumber2: !trailerNumber2Validation });
    setTrailerNumber2(event.target.value);
    setUnsavedChanges(true);
  };

  const handleProblemChange = (event, option) => {
    setValidationErrors({ ...validationErrors, problemCodeId: false });
    setProblemCodeId(option.id);
    setUnsavedChanges(true);
  };

  const handleLegShutdownChange = (event) => {
    setShutdown(!!Number(event.target.value));

    //Clear ETA validations if we set shutdown
    if (!!Number(event.target.value)) {
      setValidationErrors({ ...validationErrors, estimatedArriveTimeActual: false, estimatedArriveDateActual: false });
    }

    setUnsavedChanges(true);
  };

  const validateForm = () => {

    const localValidationErrors = {
      estimatedTimeOfArrival: false,
      location: false,
      note: false,
      startTimeActual: false,
      startDateActual: false,
      arriveTimeActual: false,
      arriveDateActual: false,
      estimatedArriveTimeActual: false,
      estimatedArriveDateActual: false,
      problemCodeId: false,
      trailerNumber1: false,
      trailerNumber2: false,
      trailerId1: false,
      trailerId2: false
    };

    // If we are missing the trailer ID show an error
    if (legUpdateType === types.legUpdateTypes.departure) {
      if (legMoveType === types.legMoveTypes.doubleTrailer || legMoveType === types.legMoveTypes.trailer) {
        if (!trailerId1) {
          localValidationErrors.trailerId1 = true;
        }
      }
      if (legMoveType === types.legMoveTypes.doubleTrailer) {
        if (!trailerId2) {
          localValidationErrors.trailerId2 = true;
        }
      }
    }

    if (legUpdateType === types.legUpdateTypes.checkCall) {
      let estimatedArriveTimeActualDestAdjustedUtc = null;

      if (location?.trim().length === 0) {
        localValidationErrors.location = true;
      }

      //Ignore ETA if Shutdown is true - Otherwise required - PBI1760893
      if ((!estimatedArriveDateActualUserLocal || !estimatedArriveTimeOnlyActualUserLocal) && !shutdown) {
        if (!estimatedArriveDateActualUserLocal) {
          localValidationErrors.estimatedArriveDateActual = true;
        }
        if (!estimatedArriveTimeOnlyActualUserLocal) {
          localValidationErrors.estimatedArriveTimeActual = true;
        }
      }
      else {
        if (estimatedArriveDateActualUserLocal && estimatedArriveTimeOnlyActualUserLocal) {
          estimatedArriveTimeActualDestAdjustedUtc = getEstimatedArrivalDateTimeUTCFromUI();
        }
        //If one value filled out but other is missing
        else if ((estimatedArriveDateActualUserLocal && !estimatedArriveTimeOnlyActualUserLocal) || (!estimatedArriveDateActualUserLocal && estimatedArriveTimeOnlyActualUserLocal)) {

          if (!estimatedArriveDateActualUserLocal) {
            localValidationErrors.estimatedArriveDateActual = true;
          }

          if (!estimatedArriveTimeOnlyActualUserLocal) {
            localValidationErrors.estimatedArriveTimeActual = true;
          }

        }
      }

      const isEstimatedArriveTimeActualOriginAdjustedUtcValidDate =
        estimatedArriveTimeActualDestAdjustedUtc === null ||
          !isValid(estimatedArriveTimeActualDestAdjustedUtc)
          ? false : true;

      if (
        isEstimatedArriveTimeActualOriginAdjustedUtcValidDate &&
        !isFuture(estimatedArriveTimeActualDestAdjustedUtc)
      ) {
        if (!isFuture(estimatedArriveDateActualUserLocal) &&
          !isSameDay(estimatedArriveDateActualUserLocal, new Date())) {
          localValidationErrors.estimatedArriveDateActual = true;
        }
        else {
          localValidationErrors.estimatedArriveTimeActual = true;
        }
      } else if (currentLeg.arriveTimeScheduledUtc !== null) {
        const arriveTimeScheduledParseISO = parseISO(
          currentLeg.arriveTimeScheduledUtc + "Z"
        );

        const timeDifferenceInMinutes = differenceInMinutes(
          estimatedArriveTimeActualDestAdjustedUtc,
          arriveTimeScheduledParseISO
        );
        if (timeDifferenceInMinutes >= 15 && problemCodeId === noProblemCode) {
          localValidationErrors.problemCodeId = true;
        }
      }

      if (problemCodeId === noProblemCode && shutdown) {
        localValidationErrors.problemCodeId = true;
      }
    }

    if (legUpdateType === types.legUpdateTypes.arrival) {
      let arriveTimeActualDestAdjustedUtc = null;

      const startTimeActualUtc = parseISO(
        currentLeg.startTimeActualUtc.endsWith("Z")
          ? currentLeg.startTimeActualUtc
          : currentLeg.startTimeActualUtc + "Z"
      );

      if (!arriveDateActualUserLocal || !arriveTimeOnlyActualUserLocal) {
        if (!arriveDateActualUserLocal) {
          localValidationErrors.arriveDateActual = true;
        }
        if (!arriveTimeOnlyActualUserLocal) {
          localValidationErrors.arriveTimeActual = true;
        }
      }
      else {
        let arriveDateTimeActualUserLocal = arriveDateActualUserLocal;
        arriveDateTimeActualUserLocal = setHours(arriveDateTimeActualUserLocal, getHours(arriveTimeOnlyActualUserLocal));
        arriveDateTimeActualUserLocal = setMinutes(arriveDateTimeActualUserLocal, getMinutes(arriveTimeOnlyActualUserLocal));

        arriveTimeActualDestAdjustedUtc = zonedTimeToUtc(
          arriveDateTimeActualUserLocal,
          currentLeg.destinationFacilityTimeZoneName
        );
      }

      const isArriveTimeActualOriginAdjustedUtcValidDate =
        arriveTimeActualDestAdjustedUtc === null ||
          !isValid(arriveTimeActualDestAdjustedUtc)
          ? false : true;

      if (isArriveTimeActualOriginAdjustedUtcValidDate &&
        (isFuture(arriveTimeActualDestAdjustedUtc) ||
          !isAfter(arriveTimeActualDestAdjustedUtc, startTimeActualUtc))
      ) {
        if (isFuture(arriveTimeActualDestAdjustedUtc)) {
          if (isFuture(arriveDateActualUserLocal)) {
            localValidationErrors.arriveDateActual = true;
          }
          else {
            localValidationErrors.arriveTimeActual = true;
          }
        }
        else if (!isAfter(arriveTimeActualDestAdjustedUtc, startTimeActualUtc)) {
          if (isAfter(arriveDateActualUserLocal, startTimeActualUtc)
            || isSameDay(arriveDateActualUserLocal, startTimeActualUtc)) {
            localValidationErrors.arriveTimeActual = true;
          }
          else {
            localValidationErrors.arriveDateActual = true;
          }
        }
      } else if (currentLeg.arriveTimeScheduledUtc !== null) {
        const arriveTimeScheduledParseISO = parseISO(
          currentLeg.arriveTimeScheduledUtc + "Z"
        );

        const timeDifferenceInMinutes = differenceInMinutes(
          arriveTimeActualDestAdjustedUtc,
          arriveTimeScheduledParseISO
        );
        if (timeDifferenceInMinutes >= 15 && problemCodeId === noProblemCode) {
          localValidationErrors.problemCodeId = true;
        }
      }
    }

    if (legUpdateType === types.legUpdateTypes.departure) {

      // Check if trailer numbers are filled out depending on move type
      if (legMoveType === types.legMoveTypes.doubleTrailer && !validateTrailerNumber(trailerNumber2)) {
        localValidationErrors.trailerNumber2 = true;
      }

      // LOGIC FOR ALL TRAILER TYPES NOW - CHANGE BACK WHEN ADD BOBTAIL BACK
      // if ((legMoveType === types.legMoveTypes.trailer || legMoveType === types.legMoveTypes.doubleTrailer) && !validateTrailerNumber(trailerNumber1)) {
      if (!validateTrailerNumber(trailerNumber1)) {
        localValidationErrors.trailerNumber1 = true;
      }

      // Minimum date value: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date#The_ECMAScript_epoch_and_timestamps
      const MIN_DATE = new Date(-8640000000000000);
      let startTimeActualOriginAdjustedUtc = null;

      const previousLegArriveTimeActual = previousLeg
        ? parseISO(
          previousLeg.arriveTimeActualUtc.endsWith("Z")
            ? previousLeg.arriveTimeActualUtc
            : previousLeg.arriveTimeActualUtc + "Z"
        )
        : MIN_DATE;

      if (!startDateActualUserLocal || !startTimeOnlyActualUserLocal) {
        if (!startDateActualUserLocal) {
          localValidationErrors.startDateActual = true;
        }
        if (!startTimeOnlyActualUserLocal) {
          localValidationErrors.startTimeActual = true;
        }
      }
      else {
        let startDateTimeActualUserLocal = startDateActualUserLocal;
        startDateTimeActualUserLocal = setHours(startDateTimeActualUserLocal, getHours(startTimeOnlyActualUserLocal));
        startDateTimeActualUserLocal = setMinutes(startDateTimeActualUserLocal, getMinutes(startTimeOnlyActualUserLocal));
        startTimeActualOriginAdjustedUtc = zonedTimeToUtc(
          startDateTimeActualUserLocal,
          currentLeg.originFacilityTimeZoneName
        );
      }

      const isStartTimeActualOriginAdjustedUtcValidDate =
        startTimeActualOriginAdjustedUtc === null ||
          !isValid(startTimeActualOriginAdjustedUtc)
          ? false : true;

      if (isStartTimeActualOriginAdjustedUtcValidDate &&
        (isFuture(startTimeActualOriginAdjustedUtc) ||
          !isAfter(startTimeActualOriginAdjustedUtc, previousLegArriveTimeActual))
      ) {
        if (isFuture(startTimeActualOriginAdjustedUtc)) {
          if (isFuture(startDateActualUserLocal)) {
            localValidationErrors.startDateActual = true;
          }
          else {
            localValidationErrors.startTimeActual = true;
          }
        } else if (!isAfter(startTimeActualOriginAdjustedUtc, previousLegArriveTimeActual)) {
          if (isAfter(startDateActualUserLocal, previousLegArriveTimeActual)
            || isSameDay(startDateActualUserLocal, previousLegArriveTimeActual)) {
            localValidationErrors.startTimeActual = true;
          }
          else {
            localValidationErrors.startDateActual = true;
          }
        }
      } else if (currentLeg.startTimeScheduledUtc) {
        const startTimeScheduledParseISO = parseISO(
          currentLeg.startTimeScheduledUtc + "Z"
        );
        const timeDifferenceInMinutes = differenceInMinutes(
          startTimeActualOriginAdjustedUtc,
          startTimeScheduledParseISO
        );
        if (timeDifferenceInMinutes >= 15 && problemCodeId === noProblemCode) {
          localValidationErrors.problemCodeId = true;
        }
      }
    }

    if (note.trim().length === 0) {
      localValidationErrors.note = true;
    }

    setValidationErrors(localValidationErrors);
    return Object.values(localValidationErrors).every((value) => !value);
  };

  const getEstimatedArrivalDateTimeUTCFromUI = () => {
    let estimatedArriveDateTimeActualUserLocal = estimatedArriveDateActualUserLocal;
    estimatedArriveDateTimeActualUserLocal = setHours(estimatedArriveDateTimeActualUserLocal, getHours(estimatedArriveTimeOnlyActualUserLocal));
    estimatedArriveDateTimeActualUserLocal = setMinutes(estimatedArriveDateTimeActualUserLocal, getMinutes(estimatedArriveTimeOnlyActualUserLocal));

    // If object is not a date, we used AutoETA and need to try and handle this as a string date
    if (Object.prototype.toString.call(estimatedArriveDateTimeActualUserLocal) !== '[object Date]' || isNaN(estimatedArriveDateTimeActualUserLocal)) {
      var dateElements = estimatedArriveDateActualUserLocal.split('/').join(' ').split(':').join(' ').split(' ');
      estimatedArriveDateTimeActualUserLocal = new Date(dateElements[2], dateElements[0] - 1, dateElements[1], dateElements[3], dateElements[4]);
    }

    return zonedTimeToUtc(
      estimatedArriveDateTimeActualUserLocal,
      currentLeg.destinationFacilityTimeZoneName
    );
  }

  const handleSaveButtonClick = async () => {
    const isValid = validateForm();

    if (isValid) {
      const trimmedNote = note.trim();

      const updates = {
        legUpdateType,
        note: {
          contents: trimmedNote,
        },
      };

      if (legUpdateType === types.legUpdateTypes.arrival) {
        let arriveDateTimeActualUserLocal = arriveDateActualUserLocal;
        arriveDateTimeActualUserLocal = setHours(arriveDateTimeActualUserLocal, getHours(arriveTimeOnlyActualUserLocal));
        arriveDateTimeActualUserLocal = setMinutes(arriveDateTimeActualUserLocal, getMinutes(arriveTimeOnlyActualUserLocal));
        updates.arriveTimeActual = zonedTimeToUtc(
          arriveDateTimeActualUserLocal,
          currentLeg.destinationFacilityTimeZoneName
        ).toISOString();
        updates.problemCodeId = problemCodeId;
      } else if (legUpdateType === types.legUpdateTypes.departure) {
        let startDateTimeActualUserLocal = startDateActualUserLocal;
        startDateTimeActualUserLocal = setHours(startDateTimeActualUserLocal, getHours(startTimeOnlyActualUserLocal));
        startDateTimeActualUserLocal = setMinutes(startDateTimeActualUserLocal, getMinutes(startTimeOnlyActualUserLocal));
        updates.startTimeActual = zonedTimeToUtc(
          startDateTimeActualUserLocal,
          currentLeg.originFacilityTimeZoneName
        ).toISOString();
        updates.problemCodeId = problemCodeId;
      } else if (legUpdateType === types.legUpdateTypes.checkCall) {
        let estimatedArriveDateTimeActualUserLocal = estimatedArriveDateActualUserLocal;

        //ETA is optional so handle accordingly
        if (estimatedArriveDateActualUserLocal !== null && estimatedArriveDateTimeActualUserLocal !== null) {
          updates.estimatedTimeOfArrival = getEstimatedArrivalDateTimeUTCFromUI().toISOString();
        }
        else {
          updates.estimatedTimeOfArrival = null;
        }

        updates.location = location;
        updates.shutdown = shutdown;
        updates.problemCodeId = problemCodeId;
      }

      setTrackingUpdateRequestStatus(types.requestStatuses.inProgress);

      // Departure has its own endpoint now
      if (legUpdateType === types.legUpdateTypes.departure) {

        // Need to structure trailers object
        let trailers = [];

        // We always push the first trailer since even bobtail needs to be BOB
        //if (legMoveType === types.legMoveTypes.trailer || legMoveType === types.legMoveTypes.doubleTrailer) {
        let trailer1 = { trailerId: trailerId1, trailerNumber: trailerNumber1 };
        trailers.push(trailer1);
        //}

        if (legMoveType === types.legMoveTypes.doubleTrailer) {
          let trailer2 = { trailerId: trailerId2, trailerNumber: trailerNumber2 };
          trailers.push(trailer2);
        }

        updates.trailers = trailers;

        await putLegDeparture(currentLeg.id, updates);
      }
      else {
        await putLegUpdate(currentLeg.id, updates);
      }
      setTrackingUpdateRequestStatus(types.requestStatuses.success);

      setLocation("");
      setNote("");
      setShowSuccessNotification(true);

      setStartDateActualUserLocal(null);
      setStartTimeOnlyActualUserLocal(null);
      setArriveDateActualUserLocal(null);
      setArriveTimeOnlyActualUserLocal(null);
      setEstimatedArriveDateActualUserLocal(null);
      setEstimatedArriveTimeOnlyActualUserLocal(null);
      // Need to set edited to false after resetting input values or it will be set to true again
      setUnsavedChanges(false);

      setLegMoveType(types.legMoveTypes.trailer);
      setTrailerNumber1(null);
      setTrailerNumber2(null);
      setTrailerId1(null);
      setTrailerId2(null);

      dispatch(fetchNotes(currentLeg.id));

      if (
        legUpdateType === types.legUpdateTypes.checkCall ||
        legUpdateType === types.legUpdateTypes.arrival ||
        legUpdateType === types.legUpdateTypes.departure
      ) {
        dispatch(fetchLegs(openedRoute.id));
        dispatch(fetchCoveredRoutes(filters));
      }
    }
  };

  useEffect(() => {
    const defaultLegUpdateType = getDefaultLegUpdateType(
      currentLeg,
      previousLeg
    );
    setDefaultLegUpdateType(defaultLegUpdateType);
    setLegUpdateType(defaultLegUpdateType);
  }, [currentLeg, openedRoute, previousLeg]);

  // On load start fetching trailer info
  useEffect(() => {
    dispatch(fetchLegTrailers(currentLeg.id));
  }, []);

  // On receiving trailer info, update fields accordingly
  useEffect(() => {
    if (legTrailers) {
      if(legTrailers.length == 0){
        setLegMoveType(types.legMoveTypes.trailer);
        setTrailerNumber1(null);
        setTrailerNumber2(null);
        setTrailerId1(null);
        setTrailerId2(null);
        setValidationErrors({ ...validationErrors, trailerId1: true, trailerId2: false });
      }
      else if (legTrailers.length == 1) {
        setLegMoveType(types.legMoveTypes.trailer);
        setTrailerNumber1(legTrailers[0].trailerNumber);
        setTrailerNumber2(null);
        setTrailerId1(legTrailers[0].trailerId);
        setTrailerId2(null);
        setValidationErrors({ ...validationErrors, trailerId1: false, trailerId2: false });
      }
      else if (legTrailers.length >= 2) {
        setLegMoveType(types.legMoveTypes.doubleTrailer);
        setTrailerNumber1(legTrailers[0].trailerNumber);
        setTrailerNumber2(legTrailers[1].trailerNumber);
        setTrailerId1(legTrailers[0].trailerId);
        setTrailerId2(legTrailers[1].trailerId);
        setValidationErrors({ ...validationErrors, trailerId1: false, trailerId2: false });
      }
    }
  }, [legTrailers]);

  const getETADateHelperMessage = () => {
    let helperMessage = undefined;

    if (validationErrors.estimatedArriveDateActual && estimatedArriveDateActualUserLocal !== null) {
      helperMessage = "Date must be in the future";
    }
    else if (validationErrors.estimatedArriveDateActual && estimatedArriveDateActualUserLocal === null) {
      if (estimatedArriveTimeOnlyActualUserLocal !== null) {
        helperMessage = "Date field is required when entering a time."
      }
      else {
        helperMessage = "Invalid date format. The date format is MM/DD/YY";
      }
    }
    else {
      helperMessage = undefined;
    }

    return helperMessage;
  }

  const getTrailerNumber1HelperMessage = () => {
    let helperMessage = undefined;

    if (validationErrors.trailerId1) {
      helperMessage = "Trailer association error. Please reload the page or reach out for support.";
    }
    else {
      if (validationErrors.trailerNumber1) {
        if (trailerNumber1.length < 1) {
          helperMessage = "Trailer number is a required field.";
        }
        else {
          helperMessage = "Invalid trailer number. Only numbers or letters allowed.";
        }
      }
    }

    return helperMessage;
  }

  const getTrailerNumber2HelperMessage = () => {
    let helperMessage = undefined;

    if (validationErrors.trailerId2) {
      helperMessage = "Trailer association error. Please reload the page or reach out for support.";
    }
    else {
      if (validationErrors.trailerNumber2) {
        if (trailerNumber2.length < 1) {
          helperMessage = "Trailer number is a required field.";
        }
        else {
          helperMessage = "Invalid trailer number. Only numbers or letters allowed.";
        }
      }
    }

    return helperMessage;
  }

  const getETATimeHelperMessage = () => {
    let helperMessage = undefined;

    if (validationErrors.estimatedArriveTimeActual && estimatedArriveTimeOnlyActualUserLocal !== null) {
      helperMessage = "Time must be in the future";
    }
    else if (validationErrors.estimatedArriveTimeActual && estimatedArriveTimeOnlyActualUserLocal === null) {
      if (estimatedArriveDateActualUserLocal !== null) {
        helperMessage = "Time field is required when entering a date."
      }
      else {
        helperMessage = "Invalid time format. The time format is HH:MM"
      }
    }
    else {
      helperMessage = undefined;
    }

    return helperMessage;
  }

  return (
    <Grid item xs={12}>
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <StyledFormControl
            variant="outlined"
            fullWidth={true}
          >
            <InputLabel id="update-type" shrink={true}>
              Update Type
            </InputLabel>
            <StyledSelect
              value={legUpdateType}
              label="Update Type"
              labelId="update-type"
              onChange={handleLegUpdateSelectChange}
              data-testid="tracking-update-leg-update-type-select"
              inputProps={{
                "data-testid": "tracking-update-leg-update-type-select-input",
              }}
            >
              {defaultLegUpdateType === types.legUpdateTypes.checkCall && [
                <MenuItem
                  value={types.legUpdateTypes.checkCall}
                  key={types.legUpdateTypes.checkCall}
                >
                  Check Call
                </MenuItem>,
                <MenuItem
                  value={types.legUpdateTypes.arrival}
                  key={types.legUpdateTypes.arrival}
                >
                  Arrival
                </MenuItem>,
              ]}
              {defaultLegUpdateType === types.legUpdateTypes.departure && (
                <MenuItem value={types.legUpdateTypes.departure}>
                  Departure
                </MenuItem>
              )}
              <MenuItem value={types.legUpdateTypes.miscellaneousNote}>
                Miscellaneous Note
              </MenuItem>
            </StyledSelect>
          </StyledFormControl>
        </Grid>
        {legUpdateType !== types.legUpdateTypes.unknown &&
          legUpdateType !== types.legUpdateTypes.miscellaneousNote && (
            <>
              {(legUpdateType === types.legUpdateTypes.checkCall ||
                legUpdateType === types.legUpdateTypes.arrival) && (
                  <Grid item xs={2}>
                    <StyledFormControl
                      variant="outlined"
                      fullWidth={true}
                      style={
                        legUpdateType === types.legUpdateTypes.arrival
                          ? { opacity: 0.5 }
                          : {}
                      }
                    >
                      <InputLabel id="shutdown" shrink={true}>
                        Shutdown
                      </InputLabel>
                      <StyledSelect
                        value={shutdown ? 1 : 0}
                        label="Shutdown"
                        labelId="shutdown"
                        onChange={handleLegShutdownChange}
                        data-testid="tracking-update-leg-update-shutdown-select"
                        inputProps={{
                          disabled:
                            legUpdateType === types.legUpdateTypes.arrival,
                          "data-testid":
                            "tracking-update-leg-update-shutdown-select-input",
                        }}
                      >
                        <MenuItem value={0} key={1}>
                          No
                        </MenuItem>
                        <MenuItem value={1} key={2}>
                          Yes
                        </MenuItem>
                      </StyledSelect>
                    </StyledFormControl>
                  </Grid>
                )}
              {(legUpdateType !== types.legUpdateTypes.departure) && (
                <Grid item xs={4}>
                  <ProblemCodeAutoComplete
                    handleChange={handleProblemChange}
                    value={problemCodeId}
                    error={validationErrors.problemCodeId}
                    autocompleteTestId="tracking-update-problem-code-autocomplete"
                  />
                </Grid>
              )}
            </>
          )}
        {legUpdateType === types.legUpdateTypes.checkCall && (
          <>
            <>
              <Grid item xs={3}>
                <LocationAutoComplete
                  autocompleteTestId="tracking-update-location-autocomplete"
                  error={validationErrors.location}
                  fullWidth={true}
                  handleChange={handleLocationChange}
                  location={location}
                />
              </Grid>
              <Grid item xs={3} style={{ alignSelf: "center" }}>
                <ThemeProvider theme={theme}>
                  <FormGroup style={{ alignSelf: "center" }}>
                    <FormControlLabel style={{ alignSelf: "center" }}
                      control={
                        <Switch color="primary" data-testid="tracking-update-auto-eta-switch" onChange={handleAutoETAChange} />
                      }
                      label="Auto-ETA" />
                  </FormGroup>
                </ThemeProvider>
              </Grid>
            </>
            <Grid item xs={3}>
              <StyledKeyboardDatePicker
                format="MM/dd/yy"
                label="ETA Date"
                placeholder="MM/DD/YY"
                variant="inline"
                inputVariant="outlined"
                initialFocusedDate={nowZonedDestinationFacility}
                minDate={nowZonedDestinationFacility}
                autoOk
                error={validationErrors.estimatedArriveDateActual}
                helperText={getETADateHelperMessage()}
                value={estimatedArriveDateActualUserLocal}
                onAccept={handleDateOrTimePickerChange(
                  "estimatedArriveDateActual",
                  setEstimatedArriveDateActualUserLocal
                )}
                onChange={handleDateOrTimePickerChange(
                  "estimatedArriveDateActual",
                  setEstimatedArriveDateActualUserLocal
                )}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  "data-testid": "tracking-update-eta-date-picker-input",
                }}
              />
              {shutdown &&
                <p style={optionalLabelStyle}>Optional</p>
              }
              {showETALoading &&
                <div style={etaDivLabel}><p style={etaLabelStyle}>Loading...</p></div>
              }
              {showETAMiles &&
                <div style={etaDivLabel}><p style={etaLabelStyle}>Est. <b>{etaMilesRemaining}</b> miles to {currentLeg.destinationFacilityAlphaCode}</p></div>
              }
            </Grid>
            <Grid item xs={3}>
              <StyledKeyboardTimePicker
                label="ETA Time"
                inputVariant="outlined"
                fullWidth={true}
                InputLabelProps={{ shrink: true }}
                placeholder="HH:MM"
                format="HH:mm"
                error={validationErrors.estimatedArriveTimeActual}
                helperText={getETATimeHelperMessage()}
                ampm={false}
                key="arrival-time"
                value={estimatedArriveTimeOnlyActualUserLocal}
                onAccept={handleDateOrTimePickerChange(
                  "estimatedArriveTimeActual",
                  setEstimatedArriveTimeOnlyActualUserLocal
                )}
                onChange={handleDateOrTimePickerChange(
                  "estimatedArriveTimeActual",
                  setEstimatedArriveTimeOnlyActualUserLocal
                )}
                variant="inline"
                keyboardIcon={customizedTimeTextField(
                  destinationFacilityTimeZoneNameShort
                )}
                KeyboardButtonProps={{ disabled: true, size: "small" }}
                inputProps={{
                  "data-testid": "tracking-update-eta-time-picker-input",
                }}
              />
              {shutdown &&
                <p style={optionalLabelStyle}>Optional</p>
              }
              {showETALoading &&
                <div style={etaDivLabel}><p style={etaLabelStyle}>Loading...</p></div>
              }
            </Grid>
          </>
        )}
        {legUpdateType === types.legUpdateTypes.arrival && (
          <>
            <Grid item xs={4}>
              <StyledKeyboardDatePicker
                format="MM/dd/yy"
                label="Actual Arrival Date"
                placeholder="MM/DD/YY"
                variant="inline"
                inputVariant="outlined"
                initialFocusedDate={nowZonedDestinationFacility}
                maxDate={nowZonedDestinationFacility}
                autoOk
                error={validationErrors.arriveDateActual}
                helperText={
                  validationErrors.arriveDateActual && arriveDateActualUserLocal !== null
                    ? "Date must be after the departure time but not in the future"
                    : validationErrors.arriveDateActual &&
                      arriveDateActualUserLocal === null
                      ? "Invalid date format. The date format is MM/DD/YY"
                      : undefined
                }
                value={arriveDateActualUserLocal}
                onAccept={handleDateOrTimePickerChange(
                  "arriveDateActual",
                  setArriveDateActualUserLocal
                )}
                onChange={handleDateOrTimePickerChange(
                  "arriveDateActual",
                  setArriveDateActualUserLocal
                )}
                InputLabelProps={{ shrink: true }}
                invalidDateMessage="Invalid Date Format. Must be MM/DD/YY"
                inputProps={{
                  "data-testid": "tracking-update-arrival-date-picker-input",
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <StyledKeyboardTimePicker
                label="Actual Arrival Time"
                inputVariant="outlined"
                fullWidth={true}
                InputLabelProps={{ shrink: true }}
                placeholder="HH:MM"
                format="HH:mm"
                error={validationErrors.arriveTimeActual}
                helperText={
                  validationErrors.arriveTimeActual &&
                    arriveTimeOnlyActualUserLocal !== null
                    ? "Time must be after the departure time but not in the future"
                    : validationErrors.arriveTimeActual &&
                      arriveTimeOnlyActualUserLocal === null
                      ? "Invalid time format. The time format is HH:MM"
                      : undefined
                }
                ampm={false}
                key="arrival-time"
                value={startTimeOnlyActualUserLocal}
                onAccept={handleDateOrTimePickerChange(
                  "arriveTimeActual",
                  setArriveTimeOnlyActualUserLocal
                )}
                onChange={handleDateOrTimePickerChange(
                  "arriveTimeActual",
                  setArriveTimeOnlyActualUserLocal
                )}
                variant="inline"
                keyboardIcon={customizedTimeTextField(
                  destinationFacilityTimeZoneNameShort
                )}
                KeyboardButtonProps={{ disabled: true, size: "small" }}
                inputProps={{
                  "data-testid": "tracking-update-arrival-time-picker-input",
                }}
              />
            </Grid>
          </>
        )}
        {legUpdateType === types.legUpdateTypes.departure && (
          <>
            { /*
            <Grid item xs={4}>
              <StyledFormControl
                variant="outlined"
                fullWidth={true}
              >
                <InputLabel id="move-type" shrink={true}>
                  Move Type
                </InputLabel>
                <StyledSelectDisabled
                  value={legMoveType}
                  label="Move Type"
                  labelId="move-type"
                  onChange={handleMoveTypeSelectChange}
                  data-testid="tracking-update-move-type-select"
                  disabled={true}
                  inputProps={{
                    "data-testid": "tracking-update-move-type-select-input",

                  }}
                >
                  <MenuItem
                    value={types.legMoveTypes.trailer}
                    key={types.legMoveTypes.trailer}
                  >
                    Trailer
                  </MenuItem>
                  <MenuItem
                    value={types.legMoveTypes.doubleTrailer}
                    key={types.legMoveTypes.doubleTrailer}
                  >
                    Double Trailer
                  </MenuItem>
                  <MenuItem
                    value={types.legMoveTypes.bobTail}
                    key={types.legMoveTypes.bobTail}
                  >
                    Bobtail
                  </MenuItem>
                </StyledSelectDisabled>
              </StyledFormControl>
              <p style={{ fontSize: "0.6em", padding: "0px", margin: "0px", color: "#f44336" }}>
                <Tooltip title="If move type is not correct, please enter an Miscellaneous Note instead of a Departure.">
                  <IconButton style={{padding: "0px"}}>
                    <StyledInfoIcon />
                  </IconButton>
                </Tooltip>
                 Move type cannot be changed at this time
                 </p>
                 </Grid>
                 */
            }
            {(legMoveType === types.legMoveTypes.trailer || legMoveType === types.legMoveTypes.doubleTrailer) && (
              <Grid item xs={6}>
                <StyledTextField
                  label="Trailer Number 1"
                  variant="outlined"
                  fullWidth={true}
                  multiline={true}
                  value={trailerNumber1}
                  helperText={getTrailerNumber1HelperMessage()}
                  onChange={handleTrailerNumber1Change}
                  error={validationErrors.trailerNumber1 || validationErrors.trailerId1}
                  inputProps={{ "data-testid": "tracking-update-trailerNumber1-text-field" }}
                />
              </Grid>
            )}
            {(legMoveType === types.legMoveTypes.doubleTrailer) && (
              <>
                <Grid item xs={4} />
                <Grid item xs={6}>
                  <StyledTextField
                    label="Trailer Number 2"
                    variant="outlined"
                    fullWidth={true}
                    multiline={true}
                    value={trailerNumber2}
                    helperText={getTrailerNumber2HelperMessage()}
                    onChange={handleTrailerNumber2Change}
                    error={validationErrors.trailerNumber2 || validationErrors.trailerId2}
                    inputProps={{ "data-testid": "tracking-update-trailerNumber2-text-field" }}
                  />
                </Grid>
              </>
            )}
            {(legMoveType === types.legMoveTypes.bobTail) && (
              <Grid item xs={6} />
            )}
            <Grid item xs={4}>
              <StyledKeyboardDatePicker
                format="MM/dd/yy"
                label="Actual Depart Date"
                placeholder="MM/DD/YY"
                variant="inline"
                inputVariant="outlined"
                initialFocusedDate={nowZonedOriginFacility}
                maxDate={nowZonedOriginFacility}
                autoOk
                error={validationErrors.startDateActual}
                helperText={
                  validationErrors.startDateActual &&
                    startDateActualUserLocal !== null
                    ? previousLeg
                      ? "Date must be after the previous leg arrival time but not in the future"
                      : "Date must not be in the future"
                    : validationErrors.startDateActual &&
                      startDateActualUserLocal === null
                      ? "Invalid date format. The date format is MM/DD/YY"
                      : undefined
                }
                value={startDateActualUserLocal}
                onAccept={handleDateOrTimePickerChange(
                  "startDateActual",
                  setStartDateActualUserLocal
                )}
                onChange={handleDateOrTimePickerChange(
                  "startDateActual",
                  setStartDateActualUserLocal
                )}
                InputLabelProps={{ shrink: true }}
                invalidDateMessage="Invalid Date Format. Must be MM/DD/YY"
                inputProps={{
                  "data-testid": "tracking-update-departure-date-picker-input",
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <StyledKeyboardTimePicker
                label="Actual Depart Time"
                inputVariant="outlined"
                fullWidth={true}
                InputLabelProps={{ shrink: true }}
                placeholder="HH:MM"
                format="HH:mm"
                error={validationErrors.startTimeActual}
                helperText={
                  validationErrors.startTimeActual &&
                    startTimeOnlyActualUserLocal !== null
                    ? previousLeg
                      ? "Time must be after the previous leg arrival time but not in the future"
                      : "Time must not be in the future"
                    : validationErrors.startTimeActual &&
                      startTimeOnlyActualUserLocal === null
                      ? "Invalid time format. The time format is HH:MM"
                      : undefined
                }
                ampm={false}
                key="departure-time"
                value={startTimeOnlyActualUserLocal}
                onAccept={handleDateOrTimePickerChange(
                  "startTimeActual",
                  setStartTimeOnlyActualUserLocal
                )}
                onChange={handleDateOrTimePickerChange(
                  "startTimeActual",
                  setStartTimeOnlyActualUserLocal
                )}
                variant="inline"
                keyboardIcon={customizedTimeTextField(
                  originFacilityTimeZoneNameShort
                )}
                KeyboardButtonProps={{ disabled: true, size: "small" }}
                inputProps={{
                  "data-testid": "tracking-update-departure-time-picker-input",
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <ProblemCodeAutoComplete
                handleChange={handleProblemChange}
                value={problemCodeId}
                error={validationErrors.problemCodeId}
                autocompleteTestId="tracking-update-problem-code-autocomplete"
              />
            </Grid>
          </>
        )}
      </Grid>
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={8}>
          <StyledTextField
            label="Note"
            variant="outlined"
            fullWidth={true}
            multiline={true}
            value={note}
            onChange={handleNoteTextFieldChange}
            error={validationErrors.note}
            inputProps={{
              "data-testid": "tracking-update-note-text-field",
              maxLength: 240,
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <BaseButton
            onClick={handleSaveButtonClick}
            fullWidth={true}
            disabled={trackingUpdateInProgress}
            startIcon={
              trackingUpdateInProgress ? (
                <CircularProgress size={15} />
              ) : undefined
            }
            data-testid="tracking-update-submit-button"
          >
            {legUpdateType === types.legUpdateTypes.miscellaneousNote
              ? "Save Note"
              : "Save Changes"}
          </BaseButton>
        </Grid>
      </Grid>
    </Grid >
  );
}

TrackingUpdateForm.propTypes = {
  currentLeg: PropTypes.shape({
    id: PropTypes.number.isRequired,
    legSequence: PropTypes.number.isRequired,
    startTimeActualUtc: PropTypes.string,
    destinationFacilityAlphaCode: PropTypes.string,
  }),
  openedRoute: PropTypes.shape({
    activeLeg: PropTypes.shape({
      movementDestinationFacility: PropTypes.shape({
        timeZoneOffset: PropTypes.number.isRequired,
      }),
    }),
    currentLeg: PropTypes.number.isRequired,
    id: PropTypes.number.isRequired,
  }),
  setShowSuccessNotification: PropTypes.func.isRequired,
  setUnsavedChanges: PropTypes.func.isRequired,
};

export default TrackingUpdateForm;
